import React from 'react';

import { TRect } from '@/types';

import { ENABLE_BBOX_ZOOM, MIN_ZOOM_AREA } from '../constants';

import { isValidBbox } from './validateBbox';

export interface BboxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ThumbStyleResult {
  containerStyle: React.CSSProperties;
  innerStyle: React.CSSProperties;
  /** Bbox position in container coordinates, present when bbox is valid. */
  bboxRect?: BboxRect;
}

export function buildThumbStyle(
  url: string,
  x: number,
  y: number,
  cellWidth: number,
  cellHeight: number,
  bbox: TRect | null | undefined,
  displayWidth?: number
): ThumbStyleResult {
  const displayScale = displayWidth != null ? displayWidth / cellWidth : 1;
  const displayW = cellWidth * displayScale;
  const displayH = cellHeight * displayScale;

  const containerStyle: React.CSSProperties = {
    width: displayW,
    height: displayH,
    overflow: 'hidden',
    position: 'relative',
  };

  const baseInner: React.CSSProperties = {
    width: cellWidth,
    height: cellHeight,
    backgroundImage: `url(${url})`,
    backgroundSize: 'auto',
    backgroundPosition: `-${x}px -${y}px`,
    backgroundRepeat: 'no-repeat',
    transformOrigin: '0 0',
  };

  if (!ENABLE_BBOX_ZOOM || !isValidBbox(bbox)) {
    const result: ThumbStyleResult = {
      containerStyle,
      innerStyle: { ...baseInner, transform: `scale(${displayScale})` },
    };
    if (isValidBbox(bbox)) {
      const [left, top, right, bottom] = bbox;
      result.bboxRect = {
        left: left * displayW,
        top: top * displayH,
        width: (right - left) * displayW,
        height: (bottom - top) * displayH,
      };
    }
    return result;
  }

  const [left, top, right, bottom] = bbox;
  const bboxPixelW = Math.max((right - left) * cellWidth, MIN_ZOOM_AREA);
  const bboxPixelH = Math.max((bottom - top) * cellHeight, MIN_ZOOM_AREA);
  const zoomScale = Math.max(cellWidth / bboxPixelW, cellHeight / bboxPixelH);
  const totalScale = displayScale * zoomScale;

  const bboxCenterX = (left + (right - left) / 2) * cellWidth;
  const bboxCenterY = (top + (bottom - top) / 2) * cellHeight;

  const translateX = displayW / 2 - bboxCenterX * totalScale;
  const translateY = displayH / 2 - bboxCenterY * totalScale;

  // Actual bbox corners in container space (using real bbox, not the MIN_ZOOM_AREA clamped one)
  const bboxRect: BboxRect = {
    left: left * cellWidth * totalScale + translateX,
    top: top * cellHeight * totalScale + translateY,
    width: (right - left) * cellWidth * totalScale,
    height: (bottom - top) * cellHeight * totalScale,
  };

  return {
    containerStyle,
    innerStyle: {
      ...baseInner,
      transform: `translate(${translateX}px, ${translateY}px) scale(${totalScale})`,
    },
    bboxRect,
  };
}
